import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import * as L from 'leaflet';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ProvisioningService } from '../services/provisioning-service/provisioning-service';
import { DeviceLogsService } from '../services/device-logs.service';
import { DeviceSnapshotsService } from '../services/device-snapshots.service';
import { DeviceConfigurationService } from '../../device-configuration/services/device-configuration.service';
import { DeviceConfigurationDtoIncoming } from '../../device-configuration/models/device-configuration.model';
import { ToastService } from '../../../shared/services/toast-service';
import { LanguageService } from '../../../core/i18n/language.service';
import { UnitService } from '../../../core/units/unit.service';
import { MeasurePipe } from '../../../core/units/measure.pipe';
import {
  DeviceCapabilityResponse,
  DeviceHeartbeatResponse,
  DeviceProvisioningResponse,
  DeviceStatusResponse,
  MessageStatusView,
} from '../models/device-provisioning-response.model';
import { DeviceLog, DeviceLogType } from '../models/device-log.model';
import { DeviceSnapshot } from '../models/device-snapshot.model';
import { TerminalComponent } from '../terminal/terminal.component';
import { LabelBadge } from '../../../shared/components/label-badge/label-badge';
import { createBasemapLayer, createLocationMarker } from '../../../shared/utils/leaflet-basemap';
import { ENV_CONFIG } from '../../../core/env-config';
import { Button } from '../../../shared/components/button/button';
import { LayoutService } from '../../layout-management/services/layout.service';
import { Layout } from '../../layout-management/models/layout.model';
import { AssignContentDialogComponent } from '../assign-content-dialog/assign-content-dialog.component';

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 5 * 60 * 1000;

/** The tabs across the top of the detail view, in display order. */
export type DeviceTab = 'overview' | 'content' | 'terminal' | 'system' | 'network' | 'display' | 'activity';

/** The runtime shape of a `Layout.components` entry (typed `unknown[]` on the
 *  model) as built by the assign-content dialog — just enough to draw a
 *  thumbnail region and a one-line caption. */
interface LayoutComponentRuntime {
  left: number;
  top: number;
  right: number;
  bottom: number;
  mediaType?: 'IMAGE' | 'PLAYLIST' | 'RT_ARR';
  contentRefs?: { fileName?: string }[];
  contentRaw?: { multimediaRef?: { fileName?: string } }[];
}

/** Reset flavours the backend accepts (see ProvisioningService.postMqttResetForDevice). */
export type ResetType = 'DATA' | 'FULL' | 'RESTART';

/** A single label/value line rendered in the definition-list cards. */
interface DetailRow {
  key: string;
  value: string;
}

/** A capability chip — a boolean feature the display does or doesn't support. */
interface CapabilityChip {
  label: string;
  supported: boolean;
}

/** Signal quality buckets, used to colour the vitals + signal cards. Kept as a
 *  small enum-like union so the template can switch on it without magic strings. */
export type SignalQuality = 'good' | 'fair' | 'poor' | 'none';

@Component({
  selector: 'app-device-overview',
  imports: [CommonModule, FormsModule, LeafletModule, TranslocoDirective, TerminalComponent, LabelBadge, MeasurePipe,
    Button, AssignContentDialogComponent,
  ],
  templateUrl: './device-overview.component.html',
})
export class DeviceOverviewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly env = inject(ENV_CONFIG);
  private readonly provisioningService = inject(ProvisioningService);
  private readonly deviceLogsService = inject(DeviceLogsService);
  private readonly deviceSnapshotsService = inject(DeviceSnapshotsService);
  private readonly deviceConfigurationService = inject(DeviceConfigurationService);
  private readonly layoutService = inject(LayoutService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  // Read in the computed VMs below so they recompute when the language flips.
  private readonly language = inject(LanguageService);
  private readonly units = inject(UnitService);
  private readonly destroy$ = new Subject<void>();
  private logsPollSub: Subscription | null = null;
  private logsPollStartedAt = 0;
  private snapshotsPollSub: Subscription | null = null;
  private snapshotsPollStartedAt = 0;

  device: DeviceProvisioningResponse | null = null;
  /** True while the device itself is being fetched by clientId (see
   *  `ngOnInit`) — distinct from `isLoading`, which covers the heartbeat/
   *  status/capability/messages fetch that only starts once `device` exists. */
  isLoadingDevice = true;
  heartbeat: DeviceHeartbeatResponse | null = null;
  status: DeviceStatusResponse | null = null;
  capability: DeviceCapabilityResponse | null = null;
  configuration: DeviceConfigurationDtoIncoming | null = null;
  messages: MessageStatusView[] = [];
  isLoading = true;
  isLoadingConfiguration = false;

  // ── UI state (tabs, modal) ─────────────────────────────────────────────────
  readonly activeTab = signal<DeviceTab>('overview');
  readonly resetModalOpen = signal(false);
  selectedResetType: ResetType = 'RESTART';
  readonly resetTypes: ResetType[] = ['RESTART', 'DATA', 'FULL'];
  isResetting = false;
  readonly removeModalOpen = signal(false);
  isRemoving = false;
  readonly assignContentDialogOpen = signal(false);
  readonly contentLayouts = signal<Layout[]>([]);

  readonly tabs: DeviceTab[] = ['overview', 'content', 'terminal', 'system', 'network', 'display', 'activity'];

  // ── Async action flags ────────────────────────────────────────────────────
  isRequestingStatus = false;
  isRequestingCapability = false;

  logs: DeviceLog[] = [];
  isLoadingLogs = false;
  isRequestingLogs = false;
  selectedLogType: DeviceLogType = 'SYSTEM';
  readonly logTypes: DeviceLogType[] = ['SYSTEM', 'APP'];
  downloadingLogId: string | null = null;

  snapshots: DeviceSnapshot[] = [];
  isLoadingSnapshots = false;
  isRequestingSnapshot = false;
  snapshotQuality = 100;
  downloadingSnapshotId: string | null = null;
  snapshotImageUrls: Record<string, string> = {};
  viewingSnapshot: DeviceSnapshot | null = null;
  latestSnapshot: DeviceSnapshot | null = null;
  isLoadingLatestSnapshot = false;
  /** Most recent successfully uploaded snapshot — shown in place of
   *  `latestSnapshot` while a newer one is still PENDING/FAILED. */
  lastUploadedSnapshot: DeviceSnapshot | null = null;

  readonly snapshotPageSize = 8;
  snapshotPage = 0;
  snapshotHasMore = false;
  snapshotMaxKnownPage = 0;

  /** Client-side paging for the Activity tab lists — the backend returns the
   *  full history, so we slice locally to keep the cards short. */
  readonly activityPageSize = 5;
  logsPage = 0;
  messagesPage = 0;

  mapOptions: L.MapOptions | null = null;
  mapLayers: L.Layer[] = [];

  ngOnInit(): void {
    const clientId = this.route.snapshot.paramMap.get('clientId');
    if (!clientId) {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }

    // A caller that already has the full device (e.g. just fetched a list)
    // may still pass it via navigation state as a fast path — skips the
    // round trip below. Everyone else (a deep link, a page refresh, browser
    // back/forward) falls through to fetching it by clientId ourselves, so
    // this page never depends on how the user arrived at it.
    const state = history.state as { device?: DeviceProvisioningResponse };
    if (state?.device) {
      this.device = state.device;
      this.isLoadingDevice = false;
      this.onDeviceLoaded(this.device);
      return;
    }

    this.provisioningService
      .getDeviceByClientId(clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.device = response.data;
          this.isLoadingDevice = false;
          this.onDeviceLoaded(this.device);
        },
        error: () => {
          this.isLoadingDevice = false;
          // `device` stays null — the template's not-found branch handles it.
        },
      });
  }

  private onDeviceLoaded(device: DeviceProvisioningResponse): void {
    this.buildMap();
    this.fetchConfiguration(device.deviceConfigurationId);
    this.fetchData(device.clientId);
    this.fetchContentSummary(device);
  }

  /** Loads every layout across the device's assigned layout groups. Drives
   *  both the Content tab's read-only summary and the header action's label
   *  ("Assign content" vs "Edit content"), so it's fetched eagerly
   *  on load rather than deferred to the Content tab being opened — that
   *  label is visible from every tab, and flipping/reflowing it only once the
   *  Content tab is first visited was the bug, not a feature. The assign
   *  dialog itself builds its drafts from scratch rather than reading these
   *  back. */
  private fetchContentSummary(device: DeviceProvisioningResponse): void {
    const groupIds = device.layoutGroupIds ?? [];
    if (!groupIds.length) {
      this.contentLayouts.set([]);
      return;
    }
    forkJoin(groupIds.map((id) => this.layoutService.getLayoutsForGroup(id)))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (responses) => this.contentLayouts.set(responses.flatMap((r) => r.data ?? [])),
        // Non-fatal: the rest of the view is unaffected, so we don't toast.
        error: () => this.contentLayouts.set([]),
      });
  }

  /** Fetches the assigned hardware configuration (name, display size, ports).
   *  Driven off `deviceConfigurationId`, which is present on the device at init,
   *  so the Display tab can render port detail without waiting on the heartbeat
   *  forkJoin. A missing/invalid id is a no-op — the tab falls back to N/A. */
  private fetchConfiguration(configId: number | null | undefined): void {
    if (configId == null) return;
    this.isLoadingConfiguration = true;
    this.deviceConfigurationService
      .getDeviceConfigurationById(configId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.configuration = response.data ?? null;
          this.isLoadingConfiguration = false;
        },
        error: () => {
          // Non-fatal: the rest of the view is unaffected, so we don't toast.
          this.isLoadingConfiguration = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.stopLogsPolling();
    this.stopSnapshotsPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ══════════════════════════════════════════════════════════════════════════
  // View models — everything the template binds to is derived here so the HTML
  // stays declarative. Each computed() reads language() so it re-translates on
  // a language switch (per the i18n conventions).
  // ══════════════════════════════════════════════════════════════════════════

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params);
  }

  get deviceId(): number | null {
    return this.heartbeat?.deviceId ?? this.status?.deviceId ?? this.capability?.deviceId ?? null;
  }

  selectTab(tab: DeviceTab): void {
    this.activeTab.set(tab);
  }

  /** Battery capacity bucket → colour class, so the bar and figure agree. */
  batteryTone(capacity: number | null | undefined): 'good' | 'warn' | 'bad' | 'none' {
    if (capacity == null) return 'none';
    if (capacity > 40) return 'good';
    if (capacity > 15) return 'warn';
    return 'bad';
  }

  /** Classify raw dBm signal strength. Thresholds mirror the usual LTE RSSI
   *  bands; anything null (no heartbeat/status yet) is 'none'. */
  signalQuality(dbm: number | null | undefined): SignalQuality {
    if (dbm == null) return 'none';
    if (dbm >= -70) return 'good';
    if (dbm >= -85) return 'fair';
    return 'poor';
  }

  signalQualityLabel(dbm: number | null | undefined): string {
    return this.t(`devices.view.signalQuality.${this.signalQuality(dbm)}`);
  }

  private na(): string {
    return this.t('common.na');
  }

  formatUptime(seconds: number | null | undefined): string {
    if (seconds == null) return this.na();
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const parts: string[] = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (!d) parts.push(`${m}m`);
    return parts.join(' ');
  }

  formatSize(bytes: number | null | undefined): string {
    if (bytes == null) return this.na();
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024;
      i++;
    }
    return `${value.toFixed(value < 10 ? 2 : 1)} ${units[i]}`;
  }

  /** Convenience: coerce a nullable primitive to a display string, N/A otherwise. */
  private show(value: string | number | null | undefined, suffix = ''): string {
    if (value == null || value === '') return this.na();
    return `${value}${suffix}`;
  }

  /** Primary location name — English entry if present, else the first
   *  available language in the flat `{ [lang]: text }` object. */
  readonly locationName = computed<string | null>(() => {
    this.language.current();
    const names = this.device?.locationName;
    if (!names) return null;
    const entries = Object.entries(names);
    if (!entries.length) return null;
    return (names['en'] ?? entries[0][1]) || null;
  });

  // ── Content tab ───────────────────────────────────────────────────────────

  /** "1 layout"/"3 layouts" · "1 component"/"4 components", reusing the assign
   *  dialog's own count phrasing so the two surfaces never drift apart. */
  private countLabelAssign(count: number, oneKey: string, manyKey: string): string {
    return count === 1
      ? this.t(`devices.assignContent.counts.${oneKey}`)
      : this.t(`devices.assignContent.counts.${manyKey}`, { count });
  }

  readonly contentSummary = computed(() => {
    this.language.current();
    const layouts = this.contentLayouts();
    const componentsCount = layouts.reduce((n, l) => n + (l.components?.length ?? 0), 0);
    const hasContent = layouts.length > 0;
    return {
      hasContent,
      layouts,
      subtitle: hasContent
        ? this.t('devices.view.content.summary', {
            layouts: this.countLabelAssign(layouts.length, 'layoutOne', 'layoutMany'),
            components: this.countLabelAssign(componentsCount, 'componentOne', 'componentMany'),
          })
        : this.t('devices.view.content.empty'),
    };
  });

  readonly assignContentLabel = computed(() =>
    this.contentSummary().hasContent
      ? this.t('devices.view.actions.editContentAssignment')
      : this.t('devices.view.actions.assignContent'),
  );

  layoutEventLabel(layout: Layout): string {
    const event = layout.activationEvent ?? 'DEFAULT';
    const label = this.t(`devices.assignContent.events.${event}.label`);
    return event === 'BUTTON_EVENT' && layout.activationButtons ? `${label} ${layout.activationButtons}` : label;
  }

  layoutDetailLine(layout: Layout): string {
    const components = (layout.components ?? []) as LayoutComponentRuntime[];
    if (!components.length) return this.t('devices.view.content.noComponents');
    const types = [...new Set(components.map((c) => this.componentTypeLabel(c)))];
    const fileNames = components.flatMap((c) => this.componentFileNames(c));
    return fileNames.length ? `${types.join(' + ')} · ${fileNames.join(', ')}` : types.join(' + ');
  }

  private componentTypeLabel(component: LayoutComponentRuntime): string {
    const key = component.mediaType === 'RT_ARR' ? 'SVG' : (component.mediaType ?? 'IMAGE');
    return this.t(`devices.assignContent.componentType.${key}`);
  }

  private componentFileNames(component: LayoutComponentRuntime): string[] {
    const fromRefs = component.contentRefs?.map((r) => r.fileName).filter((n): n is string => !!n) ?? [];
    const fromPlaylist = component.contentRaw?.map((r) => r.multimediaRef?.fileName).filter((n): n is string => !!n) ?? [];
    return [...fromRefs, ...fromPlaylist];
  }

  /** Minimum fraction of the thumbnail's width/height a component region
   *  must occupy before its type pill is legible enough to show. Below this
   *  the pill would overflow a sliver region, so it's dropped rather than
   *  shrunk further. */
  private static readonly MIN_LABEL_FRACTION = { width: 0.22, height: 0.16 };

  /** Percentage rect (+ type pill) for one component, for the Content tab's
   *  display-shaped thumbnails — proportioned against the assigned hardware
   *  configuration. */
  layoutComponentRects(
    layout: Layout,
  ): { left: string; top: string; width: string; height: string; label: string; showLabel: boolean }[] {
    const w = Math.max(this.configuration?.displayWidth ?? 0, 1);
    const h = Math.max(this.configuration?.displayHeight ?? 0, 1);
    return ((layout.components ?? []) as LayoutComponentRuntime[]).map((c) => {
      const widthFraction = Math.max((c.right - c.left) / w, 0);
      const heightFraction = Math.max((c.bottom - c.top) / h, 0);
      return {
        left: `${(c.left / w) * 100}%`,
        top: `${(c.top / h) * 100}%`,
        width: `${widthFraction * 100}%`,
        height: `${heightFraction * 100}%`,
        label: c.mediaType ?? 'IMAGE',
        showLabel:
          widthFraction >= DeviceOverviewComponent.MIN_LABEL_FRACTION.width &&
          heightFraction >= DeviceOverviewComponent.MIN_LABEL_FRACTION.height,
      };
    });
  }

  /** Opens the assign-content dialog with the device we've already loaded —
   *  the same modal the devices list opens from its row menu. The device is
   *  in hand (the page doesn't render without it), so there's no second fetch
   *  to stall on. */
  openAssignContent(): void {
    if (!this.device) return;
    this.assignContentDialogOpen.set(true);
  }

  /** Closes the dialog and refreshes the content summary so the header label
   *  ("Assign" vs "Edit") and Content tab reflect any assignment just made. */
  onAssignContentClosed(): void {
    this.assignContentDialogOpen.set(false);
    if (this.device) this.fetchContentSummary(this.device);
  }

  // ── System tab ────────────────────────────────────────────────────────────
  readonly osRows = computed<DetailRow[]>(() => {
    this.language.current();
    const s = this.status;
    return [
      { key: this.t('devices.view.os.operatingSystem'), value: this.show(s?.operatingSystem) },
      { key: this.t('devices.view.os.firmware'), value: this.show(s?.firmwareVersion) },
      { key: this.t('devices.view.os.kernel'), value: this.show(s?.kernelVersion) },
      { key: this.t('devices.view.os.upSince'), value: this.show(s?.upSince) },
      { key: this.t('devices.view.os.rtig'), value: this.show(this.capability?.rtigVersion) },
    ];
  });

  readonly resourceRows = computed<DetailRow[]>(() => {
    this.language.current();
    const s = this.status;
    return [
      { key: this.t('devices.view.resources.memory'), value: this.show(s?.memoryUsage) },
      { key: this.t('devices.view.resources.disk'), value: this.show(s?.diskFreeSpace) },
      { key: this.t('devices.view.resources.load'), value: this.show(s?.loadAverage) },
      { key: this.t('devices.view.resources.backlight'), value: this.show(s?.backlight) },
    ];
  });

  readonly envRows = computed<DetailRow[]>(() => {
    this.language.current();
    const s = this.status;
    const h = this.heartbeat;
    return [
      { key: this.t('devices.view.env.temperature'), value: this.units.format(s?.temperature ?? h?.temperature, 'temperature') },
      { key: this.t('devices.view.env.humidity'), value: this.show(s?.humidity ?? h?.humidity, ' %') },
      { key: this.t('devices.view.env.illumination'), value: this.show(h?.illumination, ' %') },
      { key: this.t('devices.view.env.batteryVoltage'), value: this.show(s?.batteryVoltage ?? h?.batteryVoltage, ' V') },
      { key: this.t('devices.view.env.batteryCapacity'), value: this.show(s?.batteryCapacity ?? h?.batteryCapacity, ' %') },
    ];
  });

  // ── Network tab ───────────────────────────────────────────────────────────
  readonly connRows = computed<DetailRow[]>(() => {
    this.language.current();
    const s = this.status;
    const h = this.heartbeat;
    return [
      { key: this.t('devices.view.net.ip'), value: this.show(s?.ipAddress ?? h?.ipAddress) },
      { key: this.t('devices.view.net.mac'), value: this.show(s?.macAddress) },
      { key: this.t('devices.view.net.type'), value: this.show(s?.networkType) },
      { key: this.t('devices.view.net.typeDetail'), value: this.show(s?.networkTypeDetail) },
      { key: this.t('devices.view.net.provider'), value: this.show(s?.networkProvider) },
    ];
  });

  readonly modemRows = computed<DetailRow[]>(() => {
    this.language.current();
    const s = this.status;
    return [
      { key: this.t('devices.view.modem.model'), value: this.show(s?.modemModel) },
      { key: this.t('devices.view.modem.imei'), value: this.show(s?.imei) },
      { key: this.t('devices.view.modem.iccid'), value: this.show(s?.iccid) },
      { key: this.t('devices.view.modem.hardware'), value: this.show(s?.modemHardwareVersion) },
      { key: this.t('devices.view.modem.firmware'), value: this.show(s?.modemFirmwareVersion) },
      { key: this.t('devices.view.modem.software'), value: this.show(s?.modemSoftwareVersion) },
    ];
  });

  // ── Display tab ───────────────────────────────────────────────────────────
  readonly screenRows = computed<DetailRow[]>(() => {
    this.language.current();
    const c = this.capability;
    const resolution =
      c?.screenWidth != null && c?.screenHeight != null
        ? `${c.screenWidth} × ${c.screenHeight} px`
        : this.na();
    return [
      { key: this.t('devices.view.screen.resolution'), value: resolution },
      { key: this.t('devices.view.screen.orientation'), value: this.show(c?.screenOrientation) },
      { key: this.t('devices.view.screen.aspectRatio'), value: this.show(c?.screenAspectRatio) },
    ];
  });

  readonly textRows = computed<DetailRow[]>(() => {
    this.language.current();
    const c = this.capability;
    const scroll =
      c?.textCanScroll == null
        ? this.na()
        : c.textCanScroll
          ? this.t('devices.view.text.scrollYes', { max: c.textMaxScroll ?? 0 })
          : this.t('devices.view.text.scrollNo');
    return [
      { key: this.t('devices.view.text.encodings'), value: this.show(c?.textCharEncodings) },
      { key: this.t('devices.view.text.fonts'), value: this.show(c?.textFonts) },
      { key: this.t('devices.view.text.lines'), value: this.show(c?.textLines) },
      { key: this.t('devices.view.text.charsPerLine'), value: this.show(c?.textCharsPerLine) },
      { key: this.t('devices.view.text.scroll'), value: scroll },
    ];
  });

  readonly capabilityChips = computed<CapabilityChip[]>(() => {
    this.language.current();
    const c = this.capability;
    const chip = (label: string, supported: boolean | null | undefined): CapabilityChip => ({
      label: this.t(label),
      supported: !!supported,
    });
    return [
      chip('devices.view.caps.icons', c?.iconsSupported),
      chip('devices.view.caps.images', c?.imagesSupported),
      chip('devices.view.caps.videos', c?.videosSupported),
      chip('devices.view.caps.playlists', c?.playlistsSupported),
      chip('devices.view.caps.carousels', c?.carouselsSupported),
      chip('devices.view.caps.continuity', c?.componentContinuitySupported),
      chip('devices.view.caps.html', c?.htmlSupported),
      chip('devices.view.caps.javascript', c?.javaScriptSupported),
      chip('devices.view.caps.css', c?.cssSupported),
    ];
  });

  readonly hardwareRows = computed<DetailRow[]>(() => {
    this.language.current();
    const c = this.configuration;
    const displaySize =
      c?.displayWidth != null && c?.displayHeight != null
        ? `${c.displayWidth} × ${c.displayHeight} px`
        : this.na();
    return [
      { key: this.t('devices.view.hardware.configName'), value: this.show(c?.name) },
      { key: this.t('devices.view.hardware.displaySize'), value: displaySize },
      { key: this.t('devices.view.hardware.timezone'), value: this.show(c?.timezone) },
      { key: this.t('devices.view.hardware.ports'), value: this.show(c?.displayPorts?.length) },
    ];
  });

  /** Semantic tone for a message status badge — drives the pill colour. */
  messageTone(status: string | null | undefined): 'ok' | 'failed' | 'pending' | 'neutral' {
    switch (status) {
      case 'OK':
        return 'ok';
      case 'FAILED':
        return 'failed';
      case 'PENDING':
      case 'DOWNLOADING':
      case 'INSTALLING':
        return 'pending';
      default:
        return 'neutral';
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Modal controls
  // ══════════════════════════════════════════════════════════════════════════

  openResetModal(): void {
    this.resetModalOpen.set(true);
  }

  closeResetModal(): void {
    this.resetModalOpen.set(false);
  }

  confirmReset(): void {
    if (!this.device || this.isResetting) return;
    this.isResetting = true;
    this.provisioningService
      .postMqttResetForDevice(this.device, this.selectedResetType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.resetSent'), 'success');
          this.isResetting = false;
          this.closeResetModal();
        },
        error: () => {
          this.toastService.show(this.t('devices.view.toast.resetFailed'), 'error');
          this.isResetting = false;
        },
      });
  }

  openRemoveModal(): void {
    this.removeModalOpen.set(true);
  }

  closeRemoveModal(): void {
    this.removeModalOpen.set(false);
  }

  confirmRemove(): void {
    if (!this.device || this.isRemoving) return;
    this.isRemoving = true;
    this.provisioningService
      .deleteDevice(this.device.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.removeSuccess'), 'success');
          this.isRemoving = false;
          this.closeRemoveModal();
          // Device is gone — return to the list rather than showing a dead detail view.
          this.goBack();
        },
        error: () => {
          this.toastService.show(this.t('devices.view.toast.removeFailed'), 'error');
          this.isRemoving = false;
        },
      });
  }

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // Data fetching (preserved from the previous implementation)
  // ══════════════════════════════════════════════════════════════════════════

  private buildMap(): void {
    const latitude = this.device?.latitude;
    const longitude = this.device?.longitude;
    if (latitude == null || longitude == null) return;

    this.mapOptions = {
      layers: [createBasemapLayer(this.env.cartoApiKey)],
      zoom: 14,
      center: L.latLng(latitude, longitude),
    };

    const marker = createLocationMarker([latitude, longitude]).bindTooltip(
      this.device!.clientId,
      { direction: 'top' },
    );
    this.mapLayers = [marker];
  }

  onMapReady(map: L.Map): void {
    setTimeout(() => {
      map.invalidateSize();
      map.setView(map.getCenter(), map.getZoom());
    });
  }

  private fetchData(clientId: string): void {
    forkJoin({
      heartbeat: this.provisioningService.getHeartbeatForDevice(clientId),
      status: this.provisioningService.getStatusForDevice(clientId),
      capability: this.provisioningService.getStoredCapabilityForDevice(clientId),
      messages: this.provisioningService.getRecentMessagesForDevice(clientId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ heartbeat, status, capability, messages }) => {
          this.heartbeat = heartbeat.data;
          this.status = status.data;
          this.capability = capability.data;
          this.messages = messages.data ?? [];
          this.isLoading = false;
          const deviceId = heartbeat.data?.deviceId ?? status.data?.deviceId ?? capability.data?.deviceId;
          if (deviceId != null) {
            this.refreshLogs(deviceId);
            this.refreshSnapshots(deviceId);
          }
        },
        error: () => {
          this.toastService.show(this.t('devices.view.toast.loadFailed'), 'error');
          this.isLoading = false;
        },
      });
  }

  refreshMessages(): void {
    if (!this.device) return;
    this.provisioningService
      .getRecentMessagesForDevice(this.device.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.messages = response.data ?? [];
          this.messagesPage = Math.min(this.messagesPage, this.messagesPageCount - 1);
        },
        error: () => this.toastService.show(this.t('devices.view.toast.messagesFailed'), 'error'),
      });
  }

  requestStatus(): void {
    if (!this.device || this.isRequestingStatus) return;
    this.isRequestingStatus = true;
    this.provisioningService
      .getMqttStatusForDevice(this.device)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.statusRequested'), 'success');
          this.isRequestingStatus = false;
        },
        error: () => {
          this.toastService.show(this.t('devices.view.toast.statusFailed'), 'error');
          this.isRequestingStatus = false;
        },
      });
  }

  requestCapability(): void {
    if (!this.device || this.isRequestingCapability) return;
    this.isRequestingCapability = true;
    this.provisioningService
      .triggerCapabilityRequest(this.device.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.capabilityRequested'), 'success');
          this.isRequestingCapability = false;
        },
        error: () => {
          this.toastService.show(this.t('devices.view.toast.capabilityFailed'), 'error');
          this.isRequestingCapability = false;
        },
      });
  }

  // ── Activity tab paging (logs + messages) ────────────────────────────────
  get pagedLogs(): DeviceLog[] {
    const start = this.logsPage * this.activityPageSize;
    return this.logs.slice(start, start + this.activityPageSize);
  }

  get logsPageCount(): number {
    return Math.max(1, Math.ceil(this.logs.length / this.activityPageSize));
  }

  prevLogsPage(): void {
    if (this.logsPage > 0) this.logsPage--;
  }

  nextLogsPage(): void {
    if (this.logsPage < this.logsPageCount - 1) this.logsPage++;
  }

  get pagedMessages(): MessageStatusView[] {
    const start = this.messagesPage * this.activityPageSize;
    return this.messages.slice(start, start + this.activityPageSize);
  }

  get messagesPageCount(): number {
    return Math.max(1, Math.ceil(this.messages.length / this.activityPageSize));
  }

  prevMessagesPage(): void {
    if (this.messagesPage > 0) this.messagesPage--;
  }

  nextMessagesPage(): void {
    if (this.messagesPage < this.messagesPageCount - 1) this.messagesPage++;
  }

  // ── Logs ──────────────────────────────────────────────────────────────────
  refreshLogs(explicitDeviceId?: number): void {
    const id = explicitDeviceId ?? this.deviceId;
    if (id == null) return;
    this.isLoadingLogs = true;
    this.deviceLogsService
      .list(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.logs = response.data ?? [];
          this.logsPage = Math.min(this.logsPage, this.logsPageCount - 1);
          this.isLoadingLogs = false;
          this.syncLogsPolling(id);
        },
        error: () => {
          this.isLoadingLogs = false;
          this.toastService.show(this.t('devices.view.toast.logsLoadFailed'), 'error');
        },
      });
  }

  requestLogs(): void {
    const id = this.deviceId;
    if (id == null) {
      this.toastService.show(this.t('devices.view.toast.deviceIdMissing'), 'error');
      return;
    }
    if (this.isRequestingLogs) return;
    this.isRequestingLogs = true;
    this.deviceLogsService
      .requestUpload(id, this.selectedLogType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.logsRequested'), 'success');
          this.refreshLogs(id);
          setTimeout(() => (this.isRequestingLogs = false), 2000);
        },
        error: (err) => {
          this.isRequestingLogs = false;
          const key = err?.status === 404 ? 'devices.view.toast.deviceNotFound' : 'devices.view.toast.logsFailed';
          this.toastService.show(this.t(key), 'error');
        },
      });
  }

  downloadLog(log: DeviceLog): void {
    if (log.status !== 'UPLOADED' || this.downloadingLogId) return;
    this.downloadingLogId = log.id;
    this.deviceLogsService
      .getDownloadUrl(log.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.downloadingLogId = null;
          if (response?.url) {
            window.open(response.url, '_blank', 'noopener');
          } else {
            this.toastService.show(this.t('devices.view.toast.noDownloadUrl'), 'error');
          }
        },
        error: () => {
          this.downloadingLogId = null;
          this.toastService.show(this.t('devices.view.toast.downloadFailed'), 'error');
        },
      });
  }

  private syncLogsPolling(deviceId: number): void {
    const hasPending = this.logs.some((l) => l.status === 'PENDING');
    if (!hasPending) {
      this.stopLogsPolling();
      return;
    }
    if (this.logsPollSub) return;
    this.logsPollStartedAt = Date.now();
    this.logsPollSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.deviceLogsService.list(deviceId)),
      )
      .subscribe({
        next: (response) => {
          this.logs = response.data ?? [];
          this.logsPage = Math.min(this.logsPage, this.logsPageCount - 1);
          const stillPending = this.logs.some((l) => l.status === 'PENDING');
          const timedOut = Date.now() - this.logsPollStartedAt >= POLL_MAX_MS;
          if (!stillPending || timedOut) {
            this.stopLogsPolling();
          }
        },
        error: () => this.stopLogsPolling(),
      });
  }

  private stopLogsPolling(): void {
    this.logsPollSub?.unsubscribe();
    this.logsPollSub = null;
  }

  // ── Snapshots ─────────────────────────────────────────────────────────────
  get displaySnapshot(): DeviceSnapshot | null {
    return this.latestSnapshot?.status === 'UPLOADED' ? this.latestSnapshot : this.lastUploadedSnapshot;
  }

  refreshSnapshots(explicitDeviceId?: number): void {
    const id = explicitDeviceId ?? this.deviceId;
    if (id == null) return;
    this.isLoadingSnapshots = true;
    this.deviceSnapshotsService
      .list(id, this.snapshotPage, this.snapshotPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.snapshots = response.data ?? [];
          this.isLoadingSnapshots = false;
          this.snapshotHasMore = this.snapshots.length === this.snapshotPageSize;
          this.snapshotMaxKnownPage = Math.max(
            this.snapshotMaxKnownPage,
            this.snapshotHasMore ? this.snapshotPage + 1 : this.snapshotPage,
          );
          this.loadSnapshotImages(this.snapshots);
          this.syncSnapshotsPolling(id);
        },
        error: () => {
          this.isLoadingSnapshots = false;
          this.toastService.show(this.t('devices.view.toast.snapshotsLoadFailed'), 'error');
        },
      });
    this.refreshLatestSnapshot(id);
  }

  refreshLatestSnapshot(explicitDeviceId?: number): void {
    const id = explicitDeviceId ?? this.deviceId;
    if (id == null) return;
    this.isLoadingLatestSnapshot = true;
    this.deviceSnapshotsService
      .getLatest(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.latestSnapshot = response.data ?? null;
          this.isLoadingLatestSnapshot = false;
          if (this.latestSnapshot) {
            this.loadSnapshotImages([this.latestSnapshot]);
          }
          if (this.latestSnapshot?.status === 'UPLOADED') {
            this.lastUploadedSnapshot = this.latestSnapshot;
          } else {
            this.refreshLastUploadedSnapshot(id);
          }
        },
        error: (err) => {
          this.isLoadingLatestSnapshot = false;
          this.latestSnapshot = err?.status === 404 ? null : this.latestSnapshot;
        },
      });
  }

  /** Newest UPLOADED snapshot to show while the true latest is pending/failed. */
  private refreshLastUploadedSnapshot(deviceId: number): void {
    this.deviceSnapshotsService
      .list(deviceId, 0, 5)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const uploaded = (response.data ?? [])
            .filter((s) => s.status === 'UPLOADED')
            .sort((a, b) => (a.requestedAt < b.requestedAt ? 1 : -1));
          this.lastUploadedSnapshot = uploaded[0] ?? null;
          if (this.lastUploadedSnapshot) {
            this.loadSnapshotImages([this.lastUploadedSnapshot]);
          }
        },
      });
  }

  private loadSnapshotImages(snapshots: DeviceSnapshot[]): void {
    snapshots
      .filter((s) => s.status === 'UPLOADED' && !this.snapshotImageUrls[s.id])
      .forEach((s) => {
        this.deviceSnapshotsService
          .getDownloadUrl(s.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response?.url) {
                this.snapshotImageUrls[s.id] = response.url;
              }
            },
          });
      });
  }

  requestSnapshot(): void {
    const id = this.deviceId;
    if (id == null) {
      this.toastService.show(this.t('devices.view.toast.deviceIdMissing'), 'error');
      return;
    }
    if (this.isRequestingSnapshot) return;
    this.isRequestingSnapshot = true;
    this.deviceSnapshotsService
      .requestSnapshot(id, this.snapshotQuality)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show(this.t('devices.view.toast.snapshotRequested'), 'success');
          this.resetSnapshotPaging();
          this.refreshSnapshots(id);
          setTimeout(() => (this.isRequestingSnapshot = false), 2000);
        },
        error: (err) => {
          this.isRequestingSnapshot = false;
          const key = err?.status === 404 ? 'devices.view.toast.deviceNotFound' : 'devices.view.toast.snapshotFailed';
          this.toastService.show(this.t(key), 'error');
        },
      });
  }

  viewSnapshot(snapshot: DeviceSnapshot): void {
    if (snapshot.status !== 'UPLOADED' || this.downloadingSnapshotId) return;
    const cachedUrl = this.snapshotImageUrls[snapshot.id];
    if (cachedUrl) {
      this.viewingSnapshot = snapshot;
      return;
    }
    this.downloadingSnapshotId = snapshot.id;
    this.deviceSnapshotsService
      .getDownloadUrl(snapshot.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.downloadingSnapshotId = null;
          if (response?.url) {
            this.snapshotImageUrls[snapshot.id] = response.url;
            this.viewingSnapshot = snapshot;
          } else {
            this.toastService.show(this.t('devices.view.toast.noDownloadUrl'), 'error');
          }
        },
        error: () => {
          this.downloadingSnapshotId = null;
          this.toastService.show(this.t('devices.view.toast.downloadFailed'), 'error');
        },
      });
  }

  closeSnapshotViewer(): void {
    this.viewingSnapshot = null;
  }

  private syncSnapshotsPolling(deviceId: number): void {
    const hasPending = this.snapshots.some((s) => s.status === 'PENDING');
    if (!hasPending) {
      this.stopSnapshotsPolling();
      return;
    }
    if (this.snapshotsPollSub) return;
    this.snapshotsPollStartedAt = Date.now();
    const page = this.snapshotPage;
    this.snapshotsPollSub = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.deviceSnapshotsService.list(deviceId, page, this.snapshotPageSize)),
      )
      .subscribe({
        next: (response) => {
          this.snapshots = response.data ?? [];
          this.snapshotHasMore = this.snapshots.length === this.snapshotPageSize;
          this.snapshotMaxKnownPage = Math.max(
            this.snapshotMaxKnownPage,
            this.snapshotHasMore ? page + 1 : page,
          );
          this.loadSnapshotImages(this.snapshots);
          if (this.latestSnapshot?.status === 'PENDING') {
            this.refreshLatestSnapshot(deviceId);
          }
          const stillPending = this.snapshots.some((s) => s.status === 'PENDING');
          const timedOut = Date.now() - this.snapshotsPollStartedAt >= POLL_MAX_MS;
          if (!stillPending || timedOut) {
            this.stopSnapshotsPolling();
          }
        },
        error: () => this.stopSnapshotsPolling(),
      });
  }

  private stopSnapshotsPolling(): void {
    this.snapshotsPollSub?.unsubscribe();
    this.snapshotsPollSub = null;
  }

  get snapshotPageNumbers(): number[] {
    return Array.from({ length: this.snapshotMaxKnownPage + 1 }, (_, i) => i);
  }

  goToSnapshotPage(page: number): void {
    if (page < 0 || page === this.snapshotPage) return;
    this.snapshotPage = page;
    this.refreshSnapshots();
  }

  prevSnapshotPage(): void {
    if (this.snapshotPage === 0) return;
    this.goToSnapshotPage(this.snapshotPage - 1);
  }

  nextSnapshotPage(): void {
    if (!this.snapshotHasMore) return;
    this.goToSnapshotPage(this.snapshotPage + 1);
  }

  private resetSnapshotPaging(): void {
    this.snapshotPage = 0;
    this.snapshotMaxKnownPage = 0;
    this.snapshotHasMore = false;
  }
}
